import { rgba } from 'polished';
import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, Redirect, Route, Switch, useLocation, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';
import { Icon, Page } from '~/components';
import { ControlTooltip } from '~/components/Form';
import { useApi, useIntegrations, useWorkspace } from '~/contexts';
import { useAuth, useIsMounted } from '~/hooks';
import { ErrorPage } from '~/routes/public/pages';
import { colors, weights } from '~/styles';
import AccountingNotes from './accounting/AccountingNotes';
import AdditionalServices from './accounting/additional-services/AdditionalServices';
import BillingSchedule from './accounting/billing-schedule/BillingSchedule';
import ProjectExpenses from './accounting/expenses/ProjectExpenses';
import OtherItems from './accounting/other-items/OtherItems';
import Prepayments from './accounting/prepayments/Prepayments';
import Products from './accounting/products/Products';
import RevenueAdjustmentLedger from './accounting/revenue-adjustments/RevenueAdjustmentLedger';
import RevenueRecognitionLedger from './accounting/revenue-recognition/RevenueRecognitionLedger';
import BudgetDashboard from './budget/BudgetDashboard';
import MonthlyBudgetDashboard from './monthly-budget/MonthlyBudgetDashboard';
import { navGap, navWidth } from './navLayout';
import getOverviewSections from './overview/sections';
import RolesSection from './team/roles/ProjectRolesTab';
import TeamSection from './team/members/ProjectMembersTab';

const Layout = styled.div`
  display: flex;
  align-items: stretch;
  gap: ${navGap};
  margin-top: 1.5rem;
  flex: 1;
`;

const Nav = styled.nav`
  flex-shrink: 0;
  width: ${navWidth};
  position: sticky;
  left: 1rem;
  z-index: 6;
  background-color: ${colors.white};
  /* White out the page gutter behind the nav and the gap ahead of it, stopping a gutter short of the fields —
     the list reaches back that far (Page.ListView pulls itself -1rem), so covering the whole gap clips it. */
  box-shadow:
    -1rem 0 0 0 ${colors.white},
    calc(${navGap} - 1rem) 0 0 0 ${colors.white};
  border-left: 1px solid ${colors.grey10};
`;

const NavItems = styled.div`
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
`;

const NavItem = styled(NavLink)`
  display: flex;
  align-items: center;
  color: ${colors.black};
  font-size: 0.875rem;
  font-weight: ${weights.normal};
  padding: 0.5rem 0.75rem;
  border-left: 3px solid transparent;
  margin-left: -1px;

  &:hover {
    color: ${colors.black};
  }

  &.active {
    border-left-color: ${colors.primary};
    background-color: ${colors.grey5};
  }

  &:not(.active):hover {
    border-left-color: ${rgba(colors.primary, 0.4)};
  }
`;

const GroupItem = styled(NavItem)`
  &.active {
    border-left-color: transparent;
    background-color: transparent;
  }

  &.active:hover {
    border-left-color: ${rgba(colors.primary, 0.4)};
  }
`;

const GroupLabel = styled.span`
  flex: 1;
`;

const Caret = styled(Icon)`
  font-size: 0.625rem;
  color: ${colors.grey40};
`;

const ChildItem = styled(NavItem)`
  font-size: 0.8125rem;
  padding-left: 1.5rem;
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;

  --section-gutter: ${navGap};

  /* The help icons sit in the gutter, under the white the nav paints past its own edge. Lift them over it. */
  ${ControlTooltip} {
    z-index: 7;
  }

  align-self: stretch;
  display: flex;
  flex-direction: column;

  ${Page.TabHeader} {
    margin-top: 0;
  }

  ${Page.TabHeader}, ${Page.Filters} {
    left: calc(1rem + ${navWidth} + ${navGap});
    max-width: calc(100cqi - 2rem - ${navWidth} - ${navGap});
  }

  /* Same shift for the list's pinned columns, minus the gutter — the list reaches back over it already
     (Page.ListView pulls itself -1rem), so counting it lands them on top of the next column. */
  --sticky-left-origin: calc(${navWidth} + ${navGap});
`;

function accountingSections(project) {
  const isFixedFee = ['fixed', 'fixed_recurring'].includes(project.billingTypeId);

  return [
    isFixedFee && {
      key: 'billing-schedule',
      name: 'Fixed Fee Billing Schedule',
      component: <BillingSchedule project={project} />,
    },
    isFixedFee &&
      ['manual', 'automated_hours', 'automated_tm_basis', 'automated_services_cost'].includes(
        project.revenueRecognitionMethod,
      ) && {
        key: 'revenue-recognition-ledger',
        name: 'Revenue Recognition Ledger',
        component: <RevenueRecognitionLedger project={project} />,
      },
    project.isBillable && { key: 'products', name: 'Products', component: <Products project={project} /> },
    project.isBillable && {
      key: 'additional-services',
      name: 'Additional Services',
      component: <AdditionalServices project={project} />,
    },
    project.isBillable && { key: 'other-items', name: 'Other Items', component: <OtherItems project={project} /> },
    project.isBillable && { key: 'prepayments', name: 'Prepayments', component: <Prepayments project={project} /> },
    {
      key: 'project-expenses',
      name: 'Project Expenses',
      component: <ProjectExpenses project={project} />,
    },
    project.isBillable && {
      key: 'revenue-adjustments',
      name: 'Earned Revenue Adjustments',
      component: <RevenueAdjustmentLedger project={project} />,
    },
  ]
    .filter(Boolean)
    .map((section) => ({
      ...section,
      component: (
        <>
          <AccountingNotes notes={project.accountingNotes} />
          {section.component}
        </>
      ),
    }));
}

export default function SettingsTab({ project, onSaved }) {
  const { path, url } = useRouteMatch();
  const location = useLocation();
  const api = useApi();
  const auth = useAuth();
  const integrations = useIntegrations();
  const isMounted = useIsMounted();
  const { workspace } = useWorkspace();

  // The Slack and QuickBooks sections are offered only where the integration is actually set up, so the nav
  // has to answer that here rather than let a member open a section with nothing in it.
  const [isSlackConnected, setIsSlackConnected] = useState(false);
  useEffect(() => {
    (async () => {
      const { data } = await api.www.workspaces(workspace.id).integrations().slack.getConnected();
      if (isMounted.current) setIsSlackConnected(data);
    })();
  }, [api, workspace.id, isMounted]);

  const isQbo =
    !!integrations?.qbo && project.accountingProvider === 'qbo' && !!project.client && !project.client.isInternal;

  const [qboProjectsEnabled, setQboProjectsEnabled] = useState(null);
  useEffect(() => {
    setQboProjectsEnabled(null);
    // Reading the connection's preferences is a workspace-manager call; without it the section stays hidden.
    if (!isQbo || !project.accountingConnectionId || !auth.workspace.manage) return;

    (async () => {
      try {
        const { data } = await api.www
          .workspaces(workspace.id)
          .qbo(project.accountingConnectionId)
          .preferences.projectsEnabled();
        if (isMounted.current) setQboProjectsEnabled(data);
      } catch {
        setQboProjectsEnabled(false);
      }
    })();
  }, [isQbo, project.accountingConnectionId, workspace.id, api, isMounted, auth.workspace.manage]);

  const sections = useMemo(() => {
    const accountingChildren = accountingSections(project);

    return [
      {
        key: 'overview',
        name: 'Overview',
        children: getOverviewSections(project, onSaved, {
          isSlackConnected,
          isQuickBooksEnabled: isQbo && !!qboProjectsEnabled,
        }),
      },
      { key: 'team', name: 'Team', component: <TeamSection project={project} /> },
      ...(project.useRoles ? [{ key: 'roles', name: 'Roles', component: <RolesSection project={project} /> }] : []),
      ...(accountingChildren.length > 0
        ? [{ key: 'accounting', name: 'Accounting', children: accountingChildren }]
        : []),
      ...(project.useBudget
        ? [{ key: 'budget', name: 'Budget', component: <BudgetDashboard project={project} /> }]
        : []),
      ...(project.useMonthlyBudget
        ? [{ key: 'monthly-budget', name: 'Monthly Budget', component: <MonthlyBudgetDashboard project={project} /> }]
        : []),
    ];
  }, [project, onSaved, isSlackConnected, isQbo, qboProjectsEnabled]);

  return (
    <Layout>
      <Nav>
        <NavItems>
          {sections.map((section) => {
            const isActive = location.pathname.startsWith(`${url}/${section.key}`);

            if (!section.children) {
              return (
                <NavItem key={section.key} to={`${url}/${section.key}`}>
                  {section.name}
                </NavItem>
              );
            }

            return (
              <React.Fragment key={section.key}>
                <GroupItem to={`${url}/${section.key}`}>
                  <GroupLabel>{section.name}</GroupLabel>
                  <Caret icon={isActive ? 'chevron-down' : 'chevron-right'} />
                </GroupItem>

                {isActive &&
                  section.children.map((child) => (
                    <ChildItem key={child.key} to={`${url}/${section.key}/${child.key}`}>
                      {child.name}
                    </ChildItem>
                  ))}
              </React.Fragment>
            );
          })}
        </NavItems>
      </Nav>

      <Content>
        <Switch>
          {sections.flatMap((section) =>
            section.children
              ? [
                  ...section.children.map((child) => (
                    <Route key={`${section.key}/${child.key}`} path={`${path}/${section.key}/${child.key}`}>
                      {child.component}
                    </Route>
                  )),
                  <Redirect
                    key={`${section.key}-index`}
                    exact
                    from={`${path}/${section.key}`}
                    to={`${url}/${section.key}/${section.children[0].key}`}
                  />,
                ]
              : [
                  <Route key={section.key} path={`${path}/${section.key}`}>
                    {section.component}
                  </Route>,
                ],
          )}

          <Redirect exact from={path} to={`${url}/overview`} />

          <Route path={path}>
            <ErrorPage.NotFound publicSite={false} />
          </Route>
        </Switch>
      </Content>
    </Layout>
  );
}
